import { arnInfo } from "@p0security/integrations-aws-shared/util";
import type { DatabaseHosting } from "@p0security/integrations-db";
import { otherInstalledIntegration } from "@p0security/integrations-installer";
import type { ElementConfigOf } from "@p0security/shared/install/types";
import type { DevEnv } from "@p0security/shared/types/environment";
import { ValidationError } from "@p0security/shared/types/error";
import { Typography } from "@p0security/ui";
import objectHash from "object-hash";

type DbIamWriteItem = {
  hosting: ElementConfigOf<typeof DatabaseHosting>;
};

type DbIamWriteConfig = Record<
  string,
  DbIamWriteItem & {
    state?: string;
  }
>;

type DbIamWriteContext = DevEnv & {
  config: { "iam-write": DbIamWriteConfig };
};

// The connector lambda is one-per-VPC; the first DB onboarded in a
// VPC installs it (pass `connectorArn`), subsequent DBs reuse it (pass
// `connectorId`). Not engine-specific.
type ConnectorTerraformParams = {
  id: string;
  type: "cluster" | "db";
  hashId: (id: string) => string;
  awsRegion: string;
} & (
  | { connectorArn: string; connectorId?: never }
  | { connectorId: string; connectorArn?: never }
);

export type SqlInstruction = {
  command: string;
  header: React.ReactNode;
};

/**
 * Builds the `iam-write` install-instructions renderer for a DB engine on AWS RDS.
 */
export const dbAwsRdsIamWriteCommands = (
  context: DbIamWriteContext,
  id: string,
  item: DbIamWriteItem & {
    hosting: Extract<DbIamWriteItem["hosting"], { type: "aws-rds" }>;
  }
) => {
  return (opts: {
    /** SQL to run before the connector's terraform is applied */
    sqlInstructions: SqlInstruction[];
    /** SQL to run after the connector's terraform is applied */
    postTfSqlInstructions?: SqlInstruction[];
    installTf: (context: DevEnv, params: ConnectorTerraformParams) => string;
  }) => {
    const { config } = context;
    const { hosting } = item;

    const thisVpc = hosting.vpcId;

    // If a connector is already installed on this VPC, use that connector's identifier.
    // "configure" and "installed" are both post-verify states, and the verifier
    // exercises the connector lambda — so by the time we land here the connector's
    // terraform has already been applied and the lambda exists in AWS.
    const othersInstalled = Object.entries(config["iam-write"]).find(
      ([otherId, c]) =>
        otherId !== id &&
        c.hosting.type === hosting.type &&
        c.hosting.vpcId === thisVpc &&
        (c.state === "configure" || c.state === "installed")
    )?.[1];

    // The find above already restricts matches to `aws-rds` hosting; this
    // narrows the union so we can read the connector ARN.
    const otherConnectorArn =
      othersInstalled?.hosting.type === "aws-rds"
        ? othersInstalled.hosting.connectorArn
        : undefined;
    const otherConnectorId = otherConnectorArn
      ? arnInfo(otherConnectorArn)?.name
      : undefined;
    const hashId = (id: string) => objectHash({ id }).slice(0, 8);

    const { connectorArn } = hosting;
    const {
      name: dbId,
      resourceType: type,
      region: awsRegion,
    } = arnInfo(hosting.instanceArn);

    if (!dbId || !awsRegion) {
      throw new ValidationError(
        "instanceArn",
        { item },
        "Invalid instance ARN"
      );
    }
    if (type !== "db" && type !== "cluster") {
      throw new ValidationError(
        "instanceArn",
        { item },
        "Not a database or a cluster"
      );
    }

    return {
      iac: [
        ...opts.sqlInstructions,
        {
          command: opts.installTf(
            context,
            otherConnectorId
              ? {
                  connectorId: otherConnectorId,
                  id: dbId,
                  type,
                  hashId,
                  awsRegion,
                }
              : { connectorArn, id: dbId, type, hashId, awsRegion }
          ),
          header: (
            <Typography.Paragraph>
              Then, add this to your Terraform and apply it:
            </Typography.Paragraph>
          ),
        },
        ...(opts.postTfSqlInstructions ?? []),
      ],
    };
  };
};

/**
 * Option providers listing the VPC IDs available to attach a new DB
 * `iam-write` install to.
 */
export const awsRdsVpcIdOptionProvider = otherInstalledIntegration({
  integration: "aws-rds",
  component: "iam-write",
});

export const gcpCloudSqlVpcIdOptionProvider = otherInstalledIntegration({
  integration: "gcp-cloudsql",
  component: "iam-write",
});
